import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { switchApifacilInstanceVersion } from "../../api/client";

function normalizeVersion(value) {
  return String(value || "").trim().toLowerCase() === "v2" ? "v2" : "v1";
}

export default function WhatsappQrConnectionVersionSection({
  channelId,
  savedVersion,
  hasSession,
  webhookUrl,
  rejeitarChamadas,
  onSwitched,
}) {
  const { t } = useTranslation("settings");
  const current = normalizeVersion(savedVersion);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resultNotice, setResultNotice] = useState(null);

  const target = pending || current;
  const confirmSteps = useMemo(() => {
    if (target === "v2") {
      return [
        t("channelForm.waQr.versionConfirmV2Step1"),
        t("channelForm.waQr.versionConfirmV2Step2"),
        t("channelForm.waQr.versionConfirmV2Step3"),
        t("channelForm.waQr.versionConfirmV2Step4"),
      ];
    }
    return [
      t("channelForm.waQr.versionConfirmV1Step1"),
      t("channelForm.waQr.versionConfirmV1Step2"),
      t("channelForm.waQr.versionConfirmV1Step3"),
      t("channelForm.waQr.versionConfirmV1Step4"),
    ];
  }, [t, target]);

  const applyChoice = async (next) => {
    if (!channelId) {
      onSwitched?.({ version: next, instancia_id: null, needs_reconnect: false, localOnly: true });
      return;
    }
    if (!hasSession) {
      onSwitched?.({ version: next, instancia_id: null, needs_reconnect: false, localOnly: true });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await switchApifacilInstanceVersion(channelId, {
        version: next,
        webhook_url: webhookUrl,
        webhook_status: webhookUrl,
        webhook_grupo: webhookUrl,
        rejeitar_chamadas: Boolean(rejeitarChamadas),
      });
      const notice = data?.notice || null;
      setResultNotice(notice);
      onSwitched?.({
        version: data?.version || next,
        instancia_id: data?.instancia_id || null,
        needs_reconnect: data?.needs_reconnect !== false,
        notice,
      });
    } catch (err) {
      setError(
        err?.message ||
          err?.response?.data?.message ||
          t("channelForm.waQr.versionSwitchError")
      );
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t("channelForm.waQr.versionTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("channelForm.waQr.versionIntro")}
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={current}
          onChange={(e) => {
            const next = normalizeVersion(e.target.value);
            if (next === current) return;
            if (!channelId || !hasSession) {
              applyChoice(next);
              return;
            }
            setPending(next);
            setResultNotice(null);
            setError(null);
          }}
        >
          <FormControlLabel
            value="v1"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mb: 1 }}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("channelForm.waQr.versionV1Label")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("channelForm.waQr.versionV1Help")}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="v2"
            control={<Radio />}
            sx={{ alignItems: "flex-start" }}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("channelForm.waQr.versionV2Label")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("channelForm.waQr.versionV2Help")}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {resultNotice?.title ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {resultNotice.title}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {(resultNotice.steps || []).map((step) => (
              <Typography key={step} component="li" variant="body2">
                {step}
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Dialog open={Boolean(pending)} onClose={() => !busy && setPending(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {target === "v2" ? t("channelForm.waQr.versionConfirmV2Title") : t("channelForm.waQr.versionConfirmV1Title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("channelForm.waQr.versionConfirmLead")}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {confirmSteps.map((step) => (
              <Typography key={step} component="li" variant="body2" sx={{ mb: 0.75 }}>
                {step}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>
            {t("channelForm.waQr.versionCancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={() => applyChoice(target)}
          >
            {busy ? <CircularProgress size={18} /> : t("channelForm.waQr.versionConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
